/**
 * Gym Branding Settings — customize TV mode and gym-facing displays.
 * Accessible from Settings for Club-tier gym owners.
 */

import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  Platform,
  Image,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import * as ImagePicker from 'expo-image-picker';
import * as Linking from 'expo-linking';
import { ArrowLeft, Check, Upload, Eye } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { supabase } from '@/lib/supabase';
import { getBrandConfig, saveBrandConfig, type BrandConfig } from '@/lib/whiteLabel';
import { resolveClubOwnerOrg } from '@/lib/orgProvisioning';
import { TYPO } from '@/constants/typography';
import { createLogger } from '@/lib/logger';

const log = createLogger('GYM_BRANDING');

const PRESET_COLORS = [
  { name: 'Gold',   hex: '#F59E0B' },
  { name: 'Blue',   hex: '#3B82F6' },
  { name: 'Green',  hex: '#22C55E' },
  { name: 'Red',    hex: '#EF4444' },
  { name: 'Purple', hex: '#A855F7' },
  { name: 'Teal',   hex: '#14B8A6' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'White',  hex: '#FFFFFF' },
];

export default function GymBrandingScreen() {
  const router = useRouter();
  const { user, profile } = useAuth();
  const { theme } = useTheme();

  const [selectedColor, setSelectedColor] = useState<string>('#F59E0B');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [effectiveGymId, setEffectiveGymId] = useState<string | null>(profile?.gym_id ?? null);

  const gymName = (profile as any)?.home_gym_name ?? 'Your Gym';

  useEffect(() => {
    const init = async () => {
      let gid: string | null = profile?.gym_id ?? null;

      if (!gid && profile?.id) {
        // Club org owners use organizations table, not gyms
        gid = await resolveClubOwnerOrg(profile.id);
      }

      setEffectiveGymId(gid);

      if (!gid) {
        setLoading(false);
        return;
      }

      const config: BrandConfig = await getBrandConfig(gid);
      setSelectedColor(config.accentColor);
      setLogoUrl(config.logoUrl);
      setLoading(false);
    };

    init();
  }, [profile?.gym_id, profile?.id]);

  const handlePickLogo = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      const msg = 'Please allow access to your photo library.';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Permission Required', msg);
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (result.canceled || !result.assets?.[0]) return;

    const asset = result.assets[0];
    setUploading(true);
    try {
      const fileName = `${effectiveGymId}-${Date.now()}.jpg`;
      const response = await fetch(asset.uri);
      const blob = await response.blob();

      const { error } = await supabase.storage
        .from('gym-logos')
        .upload(fileName, blob, { contentType: 'image/jpeg', upsert: true });

      if (error) {
        log.error('Logo upload failed:', error.message);
        const msg = error.message;
        Platform.OS === 'web' ? alert(msg) : Alert.alert('Upload Failed', msg);
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('gym-logos')
        .getPublicUrl(fileName);

      setLogoUrl(publicUrl);
    } catch (err: unknown) {
      log.error('Logo upload error:', err);
      const msg = 'Failed to upload logo. Please try again.';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Upload Error', msg);
    } finally {
      setUploading(false);
    }
  };

  const handlePreviewTV = () => {
    if (!effectiveGymId) return;
    const url = `https://tv.douevenlift.com/gym/${effectiveGymId}`;
    Linking.openURL(url);
  };

  const handleSave = async () => {
    if (!effectiveGymId) return;
    setSaving(true);
    const success = await saveBrandConfig(effectiveGymId, {
      logoUrl,
      accentColor: selectedColor,
    });
    setSaving(false);
    const msg = success
      ? 'Branding settings saved successfully.'
      : 'Could not save branding settings. Please try again.';
    if (Platform.OS === 'web') {
      alert(msg);
    } else {
      Alert.alert(success ? 'Saved' : 'Save Failed', msg);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: theme.colors.background }}>
        <ActivityIndicator size="large" color={theme.colors.accent} style={{ flex: 1 }} />
      </SafeAreaView>
    );
  }

  if (!effectiveGymId) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: theme.colors.background }} edges={['top']}>
        <LinearGradient
          colors={[theme.colors.background, theme.colors.surfaceVariant]}
          style={[styles.header, { borderBottomColor: theme.colors.border }]}>
          <View style={styles.headerTop}>
            <TouchableOpacity onPress={() => router.back()} hitSlop={12} style={{ width: 28 }}>
              <ArrowLeft size={24} color={theme.colors.text} />
            </TouchableOpacity>
            <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>BRANDING</Text>
            <View style={{ width: 28 }} />
          </View>
        </LinearGradient>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <Text style={{ color: theme.colors.textSecondary, textAlign: 'center', fontSize: 15, lineHeight: 22 }}>
            No gym associated with your account. Claim a gym to customize branding.
          </Text>
          <TouchableOpacity
            style={{ marginTop: 16, paddingVertical: 12, paddingHorizontal: 24, backgroundColor: theme.colors.accent, borderRadius: 2 }}
            onPress={() => router.push('/(gym)/claim' as any)}>
            <Text style={{ color: theme.colors.accentForeground, fontWeight: '700', fontSize: 15 }}>Claim Your Gym</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ backgroundColor: theme.colors.background, flex: 1 }} edges={['top']}>
      <LinearGradient
        colors={[theme.colors.background, theme.colors.surfaceVariant]}
        style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={styles.headerTop}>
          <TouchableOpacity onPress={() => router.back()} hitSlop={12} style={{ width: 28 }}>
            <ArrowLeft size={24} color={theme.colors.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>BRANDING</Text>
          <View style={{ width: 28 }} />
        </View>
      </LinearGradient>

      <ScrollView contentContainerStyle={styles.content}>

        {/* ── PREVIEW ── */}
        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.sectionTitle, { color: theme.colors.textTertiary }]}>PREVIEW</Text>
          <View style={[styles.previewCard, { backgroundColor: theme.colors.background, borderColor: selectedColor }]}>
            {logoUrl ? (
              <Image source={{ uri: logoUrl }} style={styles.previewLogo} resizeMode="contain" />
            ) : (
              <View style={[styles.previewLogoPlaceholder, { borderColor: theme.colors.border }]}>
                <Text style={{ color: theme.colors.textTertiary, fontSize: 12 }}>No Logo</Text>
              </View>
            )}
            <Text style={[styles.previewGymName, { color: selectedColor }]}>{gymName}</Text>
            <View style={[styles.previewAccentBar, { backgroundColor: selectedColor }]} />
          </View>
        </View>

        {/* ── LOGO UPLOAD ── */}
        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.sectionTitle, { color: theme.colors.textTertiary }]}>GYM LOGO</Text>
          <Text style={[styles.sectionDescription, { color: theme.colors.textSecondary }]}>
            Shown on TV displays and gym-facing screens. Square image recommended.
          </Text>
          <TouchableOpacity
            style={[styles.uploadButton, { borderColor: theme.colors.border, backgroundColor: theme.colors.surfaceVariant }]}
            onPress={handlePickLogo}
            disabled={uploading}>
            {uploading ? (
              <ActivityIndicator size="small" color={theme.colors.accent} />
            ) : (
              <>
                <Upload size={20} color={theme.colors.accent} />
                <Text style={[styles.uploadButtonText, { color: theme.colors.text }]}>
                  {logoUrl ? 'Replace Logo' : 'Upload Logo'}
                </Text>
              </>
            )}
          </TouchableOpacity>
          {logoUrl && (
            <TouchableOpacity
              style={{ marginTop: 10, alignItems: 'center' }}
              onPress={() => setLogoUrl(null)}>
              <Text style={{ color: '#EF4444', fontSize: 13, fontWeight: '600' }}>Remove Logo</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* ── ACCENT COLOR ── */}
        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.sectionTitle, { color: theme.colors.textTertiary }]}>ACCENT COLOR</Text>
          <Text style={[styles.sectionDescription, { color: theme.colors.textSecondary }]}>
            Used for highlights, borders, and text accents on displays.
          </Text>
          <View style={styles.colorGrid}>
            {PRESET_COLORS.map((color) => {
              const isSelected = selectedColor === color.hex;
              return (
                <TouchableOpacity
                  key={color.hex}
                  style={[
                    styles.colorCircle,
                    {
                      backgroundColor: color.hex,
                      borderColor: isSelected ? theme.colors.text : 'transparent',
                      borderWidth: isSelected ? 3 : 2,
                    },
                  ]}
                  onPress={() => setSelectedColor(color.hex)}
                  accessibilityLabel={color.name}>
                  {isSelected && (
                    <Check size={16} color={color.hex === '#FFFFFF' ? '#000' : '#fff'} strokeWidth={3} />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 14 }}>
            <View style={[styles.colorSwatch, { backgroundColor: selectedColor, borderColor: theme.colors.border }]} />
            <Text style={{ color: theme.colors.textSecondary, fontSize: 13 }}>{selectedColor}</Text>
          </View>
        </View>

        {/* ── PREVIEW TV MODE ── */}
        <TouchableOpacity
          style={[styles.previewButton, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}
          onPress={handlePreviewTV}>
          <Eye size={20} color={theme.colors.accent} />
          <Text style={[styles.previewButtonText, { color: theme.colors.text }]}>Preview TV Mode</Text>
        </TouchableOpacity>

        {/* ── SAVE ── */}
        <TouchableOpacity
          style={[styles.saveButton, { backgroundColor: theme.colors.accent, opacity: saving ? 0.7 : 1 }]}
          onPress={handleSave}
          disabled={saving}>
          <Text style={[styles.saveButtonText, { color: theme.colors.accentForeground }]}>
            {saving ? 'Saving...' : 'Save Branding'}
          </Text>
        </TouchableOpacity>

      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  header: {
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 48,
  },
  section: {
    borderRadius: 2,
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 0.8,
    marginBottom: 10,
    textTransform: 'uppercase',
  },
  sectionDescription: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 16,
  },
  previewCard: {
    borderWidth: 2,
    borderRadius: 2,
    padding: 24,
    alignItems: 'center',
    gap: 12,
  },
  previewLogo: {
    width: 80,
    height: 80,
    borderRadius: 2,
  },
  previewLogoPlaceholder: {
    width: 80,
    height: 80,
    borderWidth: 1,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewGymName: {
    fontSize: 20,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  previewAccentBar: {
    height: 3,
    width: '100%',
    borderRadius: 2,
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 14,
    borderWidth: 1,
    borderRadius: 2,
  },
  uploadButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },
  colorCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  colorSwatch: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1,
  },
  previewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 14,
    borderWidth: 1,
    borderRadius: 2,
    marginBottom: 12,
  },
  previewButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  saveButton: {
    paddingVertical: 16,
    borderRadius: 2,
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
});
